import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import { AlertCircle, Zap } from 'lucide-react';

import { useFeatures } from '../../../../context/FeatureContext';
import SettingsCard from '../SettingsCard';
import RulesPage from '../../Banking/RulesPage';
import AutoPostSettingsSection from '../../Banking/AutoPostSettingsSection';
import ConsistencyFindingsSection from '../../Banking/ConsistencyFindingsSection';
import RebuildAiHistoryControl from '../../Banking/RebuildAiHistoryControl';
import PriorsOptOutControl from '../../Banking/PriorsOptOutControl';
import {
    getCalibrationReadiness,
    formatPct,
    eligibilityBadge,
} from '../../../../services/InsightsService';

/**
 * Inställningar → Automatisering (UX redesign Phase D, doc §4.4).
 *
 * ONE home for everything config-not-daily-work that used to sprawl over
 * the Matchning sub-pages (Regler page, Insikter page) and Settings →
 * Bokföring (AI-historik card):
 *
 *   1. Regler — the regelverk manager (RulesPage embedded; the
 *      "Skapa regel?" CTA deep-links here with its prefill).
 *   2. Automatisk bokföring — earned autonomy: the calibration-readiness
 *      progress ("64 av 100 beslut") + per-class opt-in toggles, Nödstopp
 *      and consistency findings (the auto_post-gated components self-hide
 *      when the entitlement is off).
 *   3. AI-historik — the vector-corpus rebuild card (self-gates on a live
 *      vector stack).
 *   4. Dela anonym statistik — the community-priors opt-out, in exactly
 *      one place (self-hides without global_priors).
 *
 * The Insikter measurement tables (acceptance per layer, calibration
 * buckets, overview tiles) intentionally do NOT move here — they are §6
 * telemetry for ops, not user controls; the endpoints remain for a future
 * admin surface.
 */

const nf = new Intl.NumberFormat('sv-SE');

/**
 * Auto-bokföring readiness as PROGRESS, not a stats table: each class
 * shows how far it has come toward earning autonomy (Wilson gate,
 * §4.4 §3). The variant framing ("64 av 100 beslut") makes "why can't I
 * turn this on?" self-answering.
 */
function ReadinessCard({ companyId }) {
    const [state, setState] = useState({ loading: true, error: null, data: null });

    const load = useCallback(() => {
        if (!companyId) return;
        setState({ loading: true, error: null, data: null });
        getCalibrationReadiness(companyId)
            .then((data) => setState({ loading: false, error: null, data }))
            .catch((err) => setState({
                loading: false,
                error: err?.response?.data?.message || err.message || 'Kunde inte hämta beredskapen',
                data: null,
            }));
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    const gate = state.data?.gate || null;
    const rows = Array.isArray(state.data?.readiness) ? state.data.readiness : [];

    return (
        <div className="modern-card" style={{ marginBottom: 0 }}>
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Zap size={16} />
                        Beredskap
                    </h2>
                    <p className="section-subtitle">
                        Autonomi förtjänas per transaktionsklass: en klass kan bokföra
                        helt automatiskt först när dess förslag bevisat håller måttet
                        {gate
                            ? ` (träffsäkerhet med statistisk marginal ≥ ${formatPct(gate.wilson_lower_min)} över minst ${nf.format(gate.min_decisions)} beslut)`
                            : ''}
                        {' '}— och du själv slagit på den nedan.
                    </p>
                </div>
            </div>
            <div className="modern-card-body" style={{ padding: 0 }}>
                {state.loading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Laddar…
                    </div>
                ) : state.error ? (
                    <div style={{ padding: 16 }}>
                        <Alert variant="warning" className="mb-0 py-2" style={{ fontSize: '0.8125rem' }}>
                            <AlertCircle size={14} className="me-2" />
                            {state.error}
                        </Alert>
                    </div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.8125rem' }}>
                        Ännu inga beslut att mäta på — beredskapen byggs upp när förslag
                        bekräftas eller avvisas på Bankhändelser.
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <Table className="mb-0 align-middle" style={{ fontSize: '0.8125rem' }}>
                            <thead>
                                <tr style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
                                    <th style={{ paddingLeft: 16 }}>Transaktionsklass</th>
                                    <th>Framsteg</th>
                                    <th className="text-end">Träffsäkerhet</th>
                                    <th className="text-center" style={{ paddingRight: 16 }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const badge = eligibilityBadge(row);
                                    const minDecisions = gate?.min_decisions || 100;
                                    const decisions = row.decisions ?? 0;
                                    const progressPct = Math.min(100, Math.round((decisions / minDecisions) * 100));
                                    return (
                                        <tr key={`${row.engine_version}-${row.layer}-${row.transaction_class}-${row.statement_source}`}>
                                            <td style={{ fontWeight: 500, paddingLeft: 16 }}>{row.transaction_class || '–'}</td>
                                            <td style={{ minWidth: 180 }}>
                                                <div style={{ fontSize: '0.75rem', color: '#374151', marginBottom: 2 }}>
                                                    {nf.format(decisions)} av {nf.format(minDecisions)} beslut
                                                </div>
                                                <div style={{ height: 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: `${progressPct}%`,
                                                        height: '100%',
                                                        borderRadius: 999,
                                                        background: row.eligible ? '#10b981' : 'var(--primary)',
                                                        opacity: row.eligible ? 1 : 0.6,
                                                    }} />
                                                </div>
                                            </td>
                                            <td className="text-end">{formatPct(row.accept_rate)}</td>
                                            <td className="text-center" style={{ paddingRight: 16 }}>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={(
                                                        <Tooltip id={`readiness-${row.engine_version}-${row.layer}-${row.transaction_class}-${row.statement_source}`}>
                                                            {row.reason || '–'}
                                                        </Tooltip>
                                                    )}
                                                >
                                                    <Badge bg={badge.variant} style={{ fontSize: '0.6875rem', cursor: 'default' }}>
                                                        {badge.label}
                                                    </Badge>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AutomationSection({ companyId }) {
    const { features } = useFeatures();
    const hasAutoPost = Boolean(features?.auto_post);
    // Grandmother-first (owner 2026-07-28): the page IS the rules list.
    // Everything measurement-flavored — readiness stats, Nödstopp,
    // consistency findings, AI-historik rebuild, statistics sharing —

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 1 ── Regler (embedded manager, router-state prefill intact) */}
            <RulesPage embedded />

            {/* Everything on this page is automation config, and with it all
                open the page is under two screens — so there is nothing to
                hide. It used to sit behind one unstyled "Avancerat" text link.
                The blocks self-gate on their own entitlements, which is the
                gating that actually matters. */}

            {/* Beredskap, klassinställningarna och kvalitetskontrollen are one
                feature — auto-bokföring — and were three separate cards, two of
                them with near-identical titles. One card, three sections, the
                same shape as Leverantörsfakturor. */}
            <SettingsCard
                icon={Zap}
                title="Automatisk bokföring"
                subtitle="Beredskap, klassinställningar och kvalitetskontroll för bokföring utan granskning."
            >
                <div className="automation-group">
                    <ReadinessCard companyId={companyId} />
                    {hasAutoPost && (
                        <>
                            <AutoPostSettingsSection companyId={companyId} />
                            <ConsistencyFindingsSection companyId={companyId} />
                        </>
                    )}
                </div>
            </SettingsCard>

            <RebuildAiHistoryControl companyId={companyId} />
            <PriorsOptOutControl companyId={companyId} />

            {/* Card/box styles come from the promoted design-system partial
                (scss/components/_modern-card.scss, Phase E). */}
        </div>
    );
}
